/**
 * Concurrency rule: a cast is scheduled against every already-scheduled cast whose
 * cells it shares at least one with — it waits for those, then plays, then is
 * registered under each of its own cells so anything scheduled after it inherits the
 * wait. A cast with no cells cannot be proven disjoint from anything, so it waits on
 * the whole batch in flight rather than racing it.
 */

import { cellKey, normalizeCells } from '@/services/impact/topology';

function runCast(cast, waitFor, play, shouldSkip) {
  return (async () => {
    await Promise.allSettled(waitFor);
    if (shouldSkip && shouldSkip()) return null;
    try {
      return await play(cast);
    } catch (error) {
      console.warn('[impact] a cast failed mid-reel; the queue moves on:', error);
      return null;
    }
  })();
}

export function playScheduled(casts, play, after = null, shouldSkip = null) {
  const run = (async () => {
    if (after) await after;

    const list = casts || [];
    const byCell = new Map();
    const inFlight = [];

    const started = list.map(cast => {
      const cells = normalizeCells(cast?.cells);
      const waitFor = cells.length
        ? [...new Set(cells.map(c => byCell.get(cellKey(c.col, c.row))).filter(Boolean))]
        : [...inFlight];

      const played = runCast(cast, waitFor, play, shouldSkip);

      inFlight.push(played);
      if (cells.length) {
        cells.forEach(c => byCell.set(cellKey(c.col, c.row), played));
      }
      return played;
    });

    await Promise.allSettled(started);
  })();

  return run.catch(error => {
    console.warn('[impact] the cast scheduler failed mid-reel; the queue moves on:', error);
    return null;
  });
}

export default playScheduled;
